import hmacSHA256 from 'crypto-js/hmac-sha256';
import type { UseFetchOptions } from '#app';
import type { FetchContext, ResponseType } from 'ofetch';

export const useApi = <T>(
    url: string | (() => string),
    options: UseFetchOptions<T> = {}
) => {
    const config = useRuntimeConfig();

    const { $i18n } = useNuxtApp();

    const deviceCookie = useCookie('x-device-id', {
        default: () => crypto.randomUUID(),
        maxAge: 60 * 60 * 24 * 365
    });

    // Explicitly type the fetch options to satisfy the useFetch overload
    const fetchOptions: UseFetchOptions<T> = {
        baseURL: (config.public.apiBase as string),
        ...options,

        onRequest(context: FetchContext<any, ResponseType>) {
            const timestamp = Date.now().toString();
            const method = context.options.method?.toUpperCase() || 'GET';
            const bodyString = context.options.body ? JSON.stringify(context.options.body) : '';

            // 1. Generate HMAC Signature
            const message = `${timestamp}${method}${bodyString}${config.public.apiClientKey}`;
            const signature = hmacSHA256(message, config.public.apiHmacSecret as string).toString();

            // 2. Headers logic with standard Web API
            const headers = new Headers(context.options.headers);
            headers.set('x-api-key', config.public.apiClientKey as string);
            headers.set('x-signature', signature);
            headers.set('x-timestamp', timestamp);
            headers.set('x-device-id', deviceCookie.value || 'web-client');
            headers.set('Accept-Language', ($i18n.locale.value as string) || 'hy');

            context.options.headers = headers;
        }
    };

    return useFetch<T>(url, fetchOptions as any);
};